/**
 * 部门管理页面
 * 支持树形展示、增删改查
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  message,
  Tag,
  Switch,
  InputNumber,
  Select,
  TreeSelect,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  SearchOutlined,
  UserOutlined
} from '@ant-design/icons';
import {
  getDeptTree,
  createDept,
  updateDept,
  deleteDept
} from '../services/api/dept';
import { getUserPage } from '../services/api/user';
import { useModal } from '../hooks';
import SearchBar from '../components/common/SearchBar';
import EmptyWarm from '../components/common/EmptyWarm';
import LoadingWarm from '../components/common/LoadingWarm';
import type { Dept } from '../types/dept';
import './DeptManager.css';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const DeptManager: React.FC = () => {
  const modal = useModal();
  const [form] = Form.useForm();
  const [depts, setDepts] = useState<Dept[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingDept, setEditingDept] = useState<Dept | null>(null);
  const [deptTree, setDeptTree] = useState<Dept[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [userCountMap, setUserCountMap] = useState<Map<number, number>>(new Map());

  // 获取部门列表
  const fetchDepts = async () => {
    setLoading(true);
    try {
      const tree = await getDeptTree();
      setDepts(tree);
      setDeptTree(tree);

      // 获取每个部门的用户数量
      const userCountMapTemp = new Map<number, number>();
      for (const dept of tree) {
        try {
          const res = await getUserPage({ current: 1, size: 1, deptId: dept.id });
          userCountMapTemp.set(dept.id!, res.total || 0);
        } catch (e) {
          userCountMapTemp.set(dept.id!, 0);
        }
      }
      setUserCountMap(userCountMapTemp);

      // 设置展开的节点
      const keys: React.Key[] = [];
      const collectKeys = (nodes: Dept[]) => {
        nodes.forEach(node => {
          if (node.id) {
            keys.push(node.id);
          }
          if (node.children && node.children.length > 0) {
            collectKeys(node.children);
          }
        });
      };
      collectKeys(tree);
      setExpandedRowKeys(keys);
    } catch (error) {
      console.error('获取部门列表失败:', error);
      message.error('获取部门列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepts();
  }, []);

  // 删除部门
  const handleDelete = async (dept: Dept) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除部门 "${dept.name}" 吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          if (!dept.id) return;
          await deleteDept(dept.id);
          message.success('部门删除成功');
          fetchDepts();
        } catch (error: any) {
          console.error('删除部门失败:', error);
          message.error(error.message || '删除部门失败');
        }
      }
    });
  };

  // 保存部门
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const data: Dept = {
        ...values,
        status: values.status ? 1 : 0
      };

      if (editingDept) {
        await updateDept({ ...data, id: editingDept.id });
        message.success('部门更新成功');
      } else {
        await createDept(data);
        message.success('部门创建成功');
      }

      modal.hideModal();
      form.resetFields();
      setEditingDept(null);
      fetchDepts();
    } catch (error: any) {
      console.error('操作失败:', error);
      if (error.message) {
        message.error(error.message);
      }
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '🏢 部门名称',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: '👥 用户数',
      key: 'userCount',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: Dept) => (
        <Text type="secondary">
          <UserOutlined /> {userCountMap.get(record.id!) || 0} 人
        </Text>
      )
    },
    {
      title: '📊 排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      align: 'center' as const
    },
    {
      title: '📈 状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: number) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {status === 1 ? '✅ 正常' : '⏸️ 禁用'}
        </Tag>
      )
    },
    {
      title: '⚙️ 操作',
      key: 'action',
      width: 200,
      render: (_: any, record: Dept) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingDept(record);
              form.setFieldsValue({
                ...record,
                status: record.status === 1
              });
              modal.showModal();
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  // 将部门树转换为 TreeSelect 所需的格式
  const convertToTreeData = (nodes: Dept[]): any[] => {
    return nodes.map(node => ({
      title: node.name,
      value: node.id,
      key: node.id,
      children: node.children ? convertToTreeData(node.children) : []
    }));
  };

  return (
    <div className="dept-manager-container">
      <Card
        className="warm-card"
        title={
          <Space>
            <TeamOutlined className="dept-manager-title-icon" />
            <span>部门管理</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingDept(null);
              form.resetFields();
              modal.showModal();
            }}
          >
            🌸 新增部门
          </Button>
        }
        bordered={false}
      >
        {loading ? (
          <LoadingWarm type="tree" tip="部门数据正在路上~" />
        ) : (
          <Table
            dataSource={depts}
            columns={columns}
            rowKey="id"
            pagination={false}
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys(keys)
            }}
            locale={{ emptyText: <EmptyWarm type="search" /> }}
          />
        )}
      </Card>

      {/* 新增/编辑部门模态框 */}
      <Modal
        title={editingDept ? '✏️ 编辑部门' : '➕ 新增部门'}
        open={modal.visible}
        onOk={handleSave}
        onCancel={modal.handleCancel}
        confirmLoading={modal.confirmLoading}
        okText="保存"
        cancelText="取消"
        width={600}
        className="warm-modal"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="parentId"
            label="📁 父级部门"
          >
            <TreeSelect
              placeholder="请选择父级部门（不选则作为顶级部门）"
              treeData={convertToTreeData(deptTree)}
              allowClear
              treeDefaultExpandAll
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="🏢 部门名称"
            rules={[{ required: true, message: '请输入部门名称!' }]}
          >
            <Input placeholder="例如：研发部、运营部" />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="📊 排序"
            initialValue={0}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="数字越小越靠前"
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="📈 状态"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren="✅ 正常"
              unCheckedChildren="⏸️ 禁用"
              defaultChecked
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeptManager;
